import {Alert, Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {GuestAppUserService} from "crqlar-react-common/dist/services/guestApp/guestAppUser.service";
import type {GuestAppUser} from "crqlar-react-common/dist/models/guestApp/guestAppUser.model";
import type {GuestAppPreviewSession} from "crqlar-react-common/dist/models/guestApp/guestAppPreviewSession.model";
import PhoneFrame, {PHONE_SCREEN_HEIGHT, PHONE_SCREEN_WIDTH} from "./PhoneFrame";
import {isPreviewReady, previewFrameSrc, previewOrigin, previewSessionMessage} from "./guestAppPreview.util";

type PreviewState =
    | {kind: "loading"}
    | {kind: "ready"; session: GuestAppPreviewSession}
    | {kind: "noProfile"}
    | {kind: "failed"};

/**
 * The guest app, signed in as this guest, inside a phone in the dashboard.
 *
 * On opening, and on every reload, the dashboard asks the API for a fresh session as the guest,
 * then loads the organization's app in a frame. When the app says it is ready the session is
 * handed over, addressed to the app's origin only, and the app runs its normal signed-in path.
 *
 * **It is the guest, not a picture of them.** Bookings made, messages sent and requests approved
 * in here happen on the guest's account. The note above the phone says so, because that is not
 * what "preview" suggests. Every session start is audited on the API with the staff member's name.
 *
 * Each reload remounts the frame, so the app starts from nothing and a session never outlives the
 * frame it was sent to.
 */
export default function GuestAppPreview({guestAppUser, appUrl}: {guestAppUser: GuestAppUser; appUrl: string}) {
    const {t} = useTranslation();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [state, setState] = useState<PreviewState>({kind: "loading"});
    const [frameKey, setFrameKey] = useState(0);

    const origin = previewOrigin(appUrl);

    const startSession = useCallback(async () => {
        if (!guestAppUser.id) return;
        setState({kind: "loading"});
        try {
            const session = await GuestAppUserService.startPreviewSession(guestAppUser.id);
            setState({kind: "ready", session});
            setFrameKey(key => key + 1);
        } catch (e) {
            // 409 is the API saying this account has no profile, which is a state of the account
            // staff can fix, not a failure of the preview.
            const status = (e as {response?: {status?: number}})?.response?.status;
            setState(status === 409 ? {kind: "noProfile"} : {kind: "failed"});
        }
    }, [guestAppUser.id]);

    // One session per guest opened, even when React runs this effect twice: StrictMode does in
    // development, and each start is a real sign-in with its own audit entry. Reload is the
    // deliberate way to ask for another.
    const openedFor = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (openedFor.current === guestAppUser.id) return;
        openedFor.current = guestAppUser.id;
        startSession();
    }, [guestAppUser.id, startSession]);

    const session = state.kind === "ready" ? state.session : undefined;

    useEffect(() => {
        if (!session) return;

        const onMessage = (event: MessageEvent) => {
            const frameWindow = frameRef.current?.contentWindow;
            if (!isPreviewReady(event, origin, frameWindow)) return;
            frameWindow?.postMessage(previewSessionMessage(session), origin);
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [session, origin]);

    // The session carries the profile the API signed in with; the account from the list may not.
    const profile = session?.user.profile ?? guestAppUser.profile;
    const guestName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || guestAppUser.email;

    const sidePanel = (
        <Stack sx={{gap: 2, width: {xs: "100%", md: 280}, maxWidth: 420, flexShrink: 0}}>
            <Box>
                <Typography sx={{fontWeight: 600}}>
                    {t("Signed in as {{name}}", {name: guestName})}
                </Typography>
                <Typography variant={"body2"} sx={{color: "text.secondary", mt: 0.5}}>
                    {t("This is the guest's own account, not a copy. Whatever you book, send or approve here happens for them. The session ends after 30 minutes.")}
                </Typography>
            </Box>

            <Button
                variant={"outlined"}
                startIcon={<RefreshOutlinedIcon/>}
                onClick={startSession}
                disabled={state.kind === "loading"}
                sx={{alignSelf: "flex-start"}}
            >
                {t("Reload preview")}
            </Button>

            {state.kind === "noProfile" && (
                <Alert severity={"info"}>
                    {t("This app user has no profile yet, so there is no app to show. Link a profile on the Basic info tab first.")}
                </Alert>
            )}

            {state.kind === "failed" && (
                <Alert severity={"error"}>
                    {t("The preview could not be opened")}
                </Alert>
            )}
        </Stack>
    );

    return (
        /*
            Side by side from md up, so the phone gets the full height of the window instead of
            sharing it with the explanation above it. On a narrow screen there is no room beside
            the phone, and the panel stacks above it, short enough not to cost much.
        */
        <Stack
            direction={{xs: "column", md: "row"}}
            sx={{gap: {xs: 3, md: 5}, pt: 1, alignItems: {xs: "center", md: "flex-start"}, justifyContent: "center"}}
        >
            {(state.kind === "loading" || state.kind === "ready") && (
                <Box sx={{order: {xs: 1, md: 0}}}>
                    <PhoneFrame>
                        {state.kind === "ready"
                            ? (
                                <iframe
                                    key={frameKey}
                                    ref={frameRef}
                                    src={previewFrameSrc(appUrl)}
                                    title={t("App preview")}
                                    width={PHONE_SCREEN_WIDTH}
                                    height={PHONE_SCREEN_HEIGHT}
                                    style={{border: 0, display: "block"}}
                                />
                            )
                            : (
                                <Box sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    <CircularProgress size={28}/>
                                </Box>
                            )}
                    </PhoneFrame>
                </Box>
            )}

            {sidePanel}
        </Stack>
    );
}
